import React, { useState, useEffect, useRef, useCallback } from 'react'; // Added useCallback
import { db, auth } from '../firebase';
// CORRECTED: 'getDoc' has been removed from this import
import { doc, setDoc, collection, onSnapshot, query, where, getDocs, runTransaction } from 'firebase/firestore';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function HrmDashboard() {
    const [allEmployees, setAllEmployees] = useState([]);
    const [reports, setReports] = useState([]);
    const [currentReportData, setCurrentReportData] = useState([]);
    const [selectedReport, setSelectedReport] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    
    const fileInputRef = useRef(null);
    const attendanceChartRef = useRef(null);
    const otChartRef = useRef(null);

    const [attendanceChartData, setAttendanceChartData] = useState(null);
    const [otChartData, setOtChartData] = useState(null);

    const appId = 'default-hr-dashboard';
    const userId = auth.currentUser?.uid;

    // CORRECTED: Wrapped Firestore references in useCallback to stabilize them for the useEffect hook
    const getEmployeesCollection = useCallback(() => collection(db, 'artifacts', appId, 'users', userId, 'hr_employees'), [userId]);
    const getReportsCollection = useCallback(() => collection(db, 'artifacts', appId, 'users', userId, 'hr_reports'), [userId]);
    const getCounterDoc = useCallback(() => doc(db, 'artifacts', appId, 'users', userId, 'hr_counters', 'employee_id'), [userId]);

    useEffect(() => {
        if (!userId) {
            setLoading(false);
            return;
        }
        const unsubscribeEmployees = onSnapshot(getEmployeesCollection(), (snapshot) => {
            const fetchedEmployees = snapshot.docs
                .map(doc => ({ docId: doc.id, ...doc.data() }))
                .sort((a, b) => (a.empId || "").localeCompare(b.empId || ""));
            setAllEmployees(fetchedEmployees);
            setLoading(false);
        });
        const unsubscribeReports = onSnapshot(getReportsCollection(), (snapshot) => {
            const fetchedReports = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .sort((a, b) => b.id.localeCompare(a.id));
            setReports(fetchedReports);
        });
        return () => {
            unsubscribeEmployees();
            unsubscribeReports();
        };
    // CORRECTED: Added the memoized functions to the dependency array
    }, [userId, getEmployeesCollection, getReportsCollection]);

    useEffect(() => {
        if (!selectedReport) {
            clearDashboard();
            return;
        }
        const report = reports.find(r => r.id === selectedReport);
        if (report && report.data) {
            setCurrentReportData(report.data);
            updateDashboard(report.data);
        }
    }, [selectedReport, reports]);

    const findOrCreateEmployee = async (name) => {
        const existing = allEmployees.find(emp => emp.name.toLowerCase() === name.toLowerCase());
        if (existing) return existing.empId;
        const q = query(getEmployeesCollection(), where("name", "==", name));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) return querySnapshot.docs[0].data().empId;
        return await generateNewEmployee(name);
    };

    const generateNewEmployee = async (name) => {
        const counterRef = getCounterDoc();
        const year = new Date().getFullYear();
        try {
            return await runTransaction(db, async (transaction) => {
                const counterSnap = await transaction.get(counterRef);
                let lastSerial = (counterSnap.exists() && counterSnap.data().year === year) ? counterSnap.data().lastSerial : 0;
                const newSerial = lastSerial + 1;
                const newId = `OMK-${year}-${String(newSerial).padStart(3, '0')}`;
                transaction.set(counterRef, { lastSerial: newSerial, year: year });
                const newEmployeeRef = doc(getEmployeesCollection());
                transaction.set(newEmployeeRef, { empId: newId, name, joinDate: new Date().toISOString().split('T')[0], status: 'active' });
                return newId;
            });
        } catch (error) {
            console.error("Transaction failed: ", error);
            throw new Error("Failed to generate new employee ID.");
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const monthYear = prompt("Please enter the report period (e.g., SEP-2025):", "SEP-2025");
        if (!monthYear) return;

        setLoadingMessage(`Processing ${file.name}...`);
        setLoading(true);
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const text = e.target.result;
                const lines = text.split('\n');
                const parsedData = [];
                for (let i = 4; i < lines.length; i++) {
                    const line = lines[i].trim();
                    if (!line) continue;
                    const parts = line.split(',');
                    if (parts.length >= 7 && parts[1].trim()) {
                        parsedData.push({ name: parts[1].trim(), present: parseFloat(parts[2]) || 0, absent: parseFloat(parts[5]) || 0, ot: parseFloat(parts[6]) || 0 });
                    }
                }
                if (parsedData.length === 0) throw new Error("No valid data rows found.");
                const reportData = [];
                for (const row of parsedData) {
                    const empId = await findOrCreateEmployee(row.name);
                    reportData.push({ ...row, empId });
                }
                await setDoc(doc(getReportsCollection(), monthYear), { createdAt: new Date().toISOString(), data: reportData });
                alert(`Successfully processed and saved report for ${monthYear}.`);
            } catch (error) {
                console.error("Error processing file:", error);
                alert(`Failed to process file: ${error.message}`);
            } finally {
                setLoading(false);
                if (fileInputRef.current) fileInputRef.current.value = null;
            }
        };
        reader.readAsText(file);
    };
    
    const updateDashboard = (data) => {
        const avgPresent = data.reduce((acc, emp) => acc + emp.present, 0) / data.length;
        const avgAbsent = data.reduce((acc, emp) => acc + emp.absent, 0) / data.length;
        setAttendanceChartData({ labels: ['Average Days'], datasets: [ { label: 'Present', data: [avgPresent], backgroundColor: 'rgba(54, 162, 235, 0.6)' }, { label: 'Absent', data: [avgAbsent], backgroundColor: 'rgba(255, 99, 132, 0.6)' } ] });
        const otData = [...data].sort((a, b) => b.ot - a.ot).slice(0, 5);
        setOtChartData({ labels: otData.map(emp => emp.name), datasets: [{ label: 'O.T. in Hrs', data: otData.map(emp => emp.ot), backgroundColor: 'rgba(75, 192, 192, 0.6)' }] });
    };

    const clearDashboard = () => {
        setAttendanceChartData(null);
        setOtChartData(null);
        setCurrentReportData([]);
    };

    const downloadPDFReport = () => {
        if (currentReportData.length === 0) {
            alert("No report data selected to download.");
            return;
        }
        const pdf = new jsPDF('p', 'mm', 'a4');
        const reportName = selectedReport;
        const page_width = pdf.internal.pageSize.getWidth();
        let finalY = 0;
        pdf.setFontSize(18);
        pdf.text(`Attendance Report: ${reportName}`, 14, 22);
        finalY = 30;
        pdf.setFontSize(14);
        pdf.text("Infographics", 14, finalY + 5);
        finalY += 10;
        const attendanceChart = attendanceChartRef.current;
        const otChart = otChartRef.current;
        if (attendanceChart && otChart) {
            const attendanceImg = attendanceChart.toBase64Image('image/png', 1);
            const otImg = otChart.toBase64Image('image/png', 1);
            pdf.addImage(attendanceImg, 'PNG', 14, finalY, 85, 50);
            pdf.addImage(otImg, 'PNG', page_width - 14 - 85, finalY, 85, 50);
            finalY += 60;
        }
        pdf.setFontSize(14);
        pdf.text("Monthly Highlights", 14, finalY);
        finalY += 5;
        const topAttendees = [...currentReportData].sort((a, b) => a.absent - b.absent).slice(0, 5);
        const lowAttendees = [...currentReportData].sort((a, b) => b.absent - a.absent).slice(0, 3);
        autoTable(pdf, {
            body: [ { top: { content: 'Top 5 Attendee', styles: { fontStyle: 'bold' } }, low: { content: 'Top 3 Low Attendance', styles: { fontStyle: 'bold' } } }, ...Array(Math.max(topAttendees.length, lowAttendees.length)).fill().map((_, i) => ({ top: topAttendees[i] ? `${topAttendees[i].name} (${topAttendees[i].absent} absent)` : '', low: lowAttendees[i] ? `${lowAttendees[i].name} (${lowAttendees[i].absent} absent)` : '' })) ],
            columns: [{ header: '', dataKey: 'top' }, { header: '', dataKey: 'low' }],
            startY: finalY, theme: 'grid', styles: { cellPadding: 2, fontSize: 9 }, columnStyles: { top: { cellWidth: (page_width - 28) / 2 }, low: { cellWidth: (page_width - 28) / 2 } },
        });
        finalY = pdf.lastAutoTable.finalY + 10;
        pdf.setFontSize(14);
        pdf.text("Full Attendance Data", 14, finalY);
        autoTable(pdf, { head: [["Employee Name", "Employee ID", "Present Days", "Absent Days", "OT Hours"]], body: currentReportData.map(item => [item.name, item.empId, item.present, item.absent, item.ot]), startY: finalY + 5 });
        pdf.save(`Attendance_Report_${reportName}.pdf`);
    };
    
    if (loading) return <div>{loadingMessage}</div>;
    if (!userId) return <div>Error: User not authenticated.</div>;
    
    const sortedByAbsentDesc = [...currentReportData].sort((a, b) => b.absent - a.absent);
    const sortedByAbsentAsc = [...currentReportData].sort((a, b) => a.absent - b.absent);
    
    return (
        <div style={{ fontFamily: 'sans-serif', backgroundColor: '#f3f4f6', padding: '2rem' }}>
            <header style={{ paddingBottom: '1rem', borderBottom: '1px solid #d1d5db', marginBottom: '1.5rem' }}>
                <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#1f2937' }}>HR Database & Attendance Dashboard</h1>
            </header>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1.5rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>1. Upload Data</h2>
                        <button onClick={() => fileInputRef.current.click()} style={{ width: '100%', backgroundColor: '#2563eb', color: 'white', padding: '0.5rem 1rem', borderRadius: '0.5rem', cursor: 'pointer' }}>Upload CSV</button>
                        <input type="file" ref={fileInputRef} onChange={handleFileUpload} style={{ display: 'none' }} accept=".csv"/>
                    </div>
                    <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                         <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>2. Employee Database</h2>
                         <ul style={{ height: '16rem', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.5rem', listStyle: 'none' }}>
                            {allEmployees.map(emp => (<li key={emp.docId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem' }}><div><span style={{ fontWeight: '500' }}>{emp.name}</span><span style={{ display: 'block', fontSize: '0.875rem', color: '#4b5563' }}>{emp.empId}</span></div></li>))}
                         </ul>
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>Dashboard: {selectedReport || 'No Report'}</h2>
                        <div style={{display: 'flex', gap: '1rem', alignItems: 'center'}}>
                            <select value={selectedReport} onChange={(e) => setSelectedReport(e.target.value)} style={{ flexGrow: 1, padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.5rem' }}><option value="">-- Select report --</option>{reports.map(r => <option key={r.id} value={r.id}>{r.id}</option>)}</select>
                            <button onClick={downloadPDFReport} disabled={!selectedReport} style={{ backgroundColor: '#16a34a', color: 'white', padding: '0.5rem 1rem', borderRadius: '0.5rem', cursor: 'pointer', opacity: !selectedReport ? 0.5 : 1 }}>Download PDF</button>
                        </div>
                    </div>
                    <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', textAlign: 'center' }}>Monthly Notice Board</h2>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                            <div>
                                <h3 style={{fontWeight: 600, color: '#991b1b', marginBottom: '0.5rem'}}>Top 3 Low Attendance</h3>
                                <ul style={{listStyle: 'none', padding: 0}}>{sortedByAbsentDesc.slice(0, 3).map(e => <li key={e.empId}>{e.name} ({e.absent} absent)</li>)}</ul>
                            </div>
                            <div>
                                <h3 style={{fontWeight: 600, color: '#166534', marginBottom: '0.5rem'}}>Top 5 Attendee</h3>
                                <ul style={{listStyle: 'none', padding: 0}}>{sortedByAbsentAsc.slice(0, 5).map(e => <li key={e.empId}>{e.name} ({e.absent} absent)</li>)}</ul>
                            </div>
                        </div>
                    </div>
                    <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>Infographics</h2>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', height: '250px' }}>
                            <div style={{ position: 'relative' }}>
                                {attendanceChartData && <Bar ref={attendanceChartRef} data={attendanceChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { title: { display: true, text: 'Average Attendance' }}}} />}
                            </div>
                            <div style={{ position: 'relative' }}>
                                {otChartData && <Bar ref={otChartRef} data={otChartData} options={{ responsive: true, maintainAspectRatio: false, indexAxis: 'y', plugins: { title: { display: true, text: 'Top 5 by Overtime' }}}} />}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default HrmDashboard;